import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import API from '../utils/api';

// Crear el contexto de autenticación
const AuthContext = createContext();

// Hook personalizado para usar el contexto
export function useAuth() {
  return useContext(AuthContext);
}

// Proveedor del contexto
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Limpiar sesión al cargar la aplicación (siempre iniciar deslogeado)
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        // Limpiar cualquier sesión guardada para forzar login
        API.auth.logout();
        setCurrentUser(null);
      } catch (error) {
        console.error("Error al limpiar estado de autenticación:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Función para iniciar sesión - usamos useCallback para memorizarla
  const login = useCallback(async (username, password) => {
    try {
      const response = await API.auth.login(username, password);

      if (response.success) {
        setCurrentUser(response.data.usuario);
        return response.data;
      } else {
        throw new Error(response.message || 'Error de autenticación');
      }
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }, []);

  // Función para cerrar sesión - también memorizada
  const logout = useCallback(() => {
    API.auth.logout();
    setCurrentUser(null);
  }, []);

  // Verificar si el usuario tiene un permiso específico - memorizada
  const hasPermission = useCallback((permiso) => {
    return API.auth.hasPermission(permiso);
  }, []);

  // Memorizamos todo el objeto value para evitar recreaciones
  const value = React.useMemo(() => ({
    currentUser,
    setCurrentUser,
    login,
    logout,
    hasPermission,
    loading
  }), [currentUser, login, logout, hasPermission, loading]);

  // Renderizado condicional para evitar montaje parcial
  if (loading) {
    return null; // O un componente de carga
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;